import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import robot from "../../assets/robot1.png";
import mouUniversal from "../../assets/mouUniversal.png";

const defaultTheme = {
  background: "bg-[#0346a4]",
  panelBg: "bg-white",
  inputBg: "bg-gray-100",
  accentText: "text-blue-700",
  mutedText: "text-gray-600",
  buttonPrimary: "bg-blue-700 hover:bg-blue-800",
  buttonText: "text-white",
  toggleActiveBg: "bg-blue-700",
  toggleInactiveBg: "bg-transparent",
  toggleActiveText: "text-white",
  toggleInactiveText: "text-blue-700",
  link: "text-blue-700 hover:underline",
  mfaBannerBg: "bg-blue-700",
};

const emptyRegisterForm = {
  correoElectronico: "",
  password: "",
  confirmPassword: "",
  primerNombre: "",
  segundoNombre: "",
  primerApellido: "",
  segundoApellido: "",
};

const AuthPageTemplate = ({
  variant,
  theme: themeProp,
  title = "Bienvenido",
  redirectPath,
}) => {
  const theme = useMemo(() => ({ ...defaultTheme, ...themeProp }), [themeProp]);
  const {
    login,
    register,
    verifyMfa,
    hasPendingMfa,
    isAuthenticated,
    cancelPendingMfa,
  } = useAuth();
  const [tab, setTab] = useState("login"); // login | register | mfa
  const [loginForm, setLoginForm] = useState({
    correoElectronico: "",
    password: "",
  });
  const [registerForm, setRegisterForm] = useState(emptyRegisterForm);
  const [mfaCode, setMfaCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && redirectPath) {
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectPath]);

  useEffect(() => {
    if (hasPendingMfa) {
      setTab("mfa");
    } else if (tab === "mfa") {
      setTab("login");
    }
  }, [hasPendingMfa, tab]);

  const updateLoginForm = (field) => (event) => {
    setLoginForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const updateRegisterForm = (field) => (event) => {
    setRegisterForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setMessage(null);
    if (!loginForm.correoElectronico || !loginForm.password) {
      setMessage({ type: "error", text: "Ingresa tu correo y contraseña." });
      return;
    }
    try {
      setIsSubmitting(true);
      const result = await login(
        loginForm.correoElectronico.trim(),
        loginForm.password,
      );
      if (!result.requiresMfa) {
        navigate(redirectPath || "/", { replace: true });
      } else {
        setMessage({
          type: "info",
          text: "Ingresa tu código de autenticación para continuar.",
        });
        setTab("mfa");
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "No se pudo iniciar sesión.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    setMessage(null);

    if (!registerForm.correoElectronico || !registerForm.password) {
      setMessage({
        type: "error",
        text: "El correo y la contraseña son obligatorios.",
      });
      return;
    }

    if (!registerForm.primerNombre || !registerForm.primerApellido) {
      setMessage({
        type: "error",
        text: "Debes indicar al menos tu primer nombre y primer apellido.",
      });
      return;
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      setMessage({
        type: "error",
        text: "Las contraseñas no coinciden.",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      await register({
        correoElectronico: registerForm.correoElectronico.trim(),
        password: registerForm.password,
        primerNombre: registerForm.primerNombre.trim(),
        segundoNombre: registerForm.segundoNombre.trim() || undefined,
        primerApellido: registerForm.primerApellido.trim(),
        segundoApellido: registerForm.segundoApellido.trim() || undefined,
      });
      setMessage({
        type: "success",
        text: "Registro exitoso. Ahora inicia sesión.",
      });
      setRegisterForm(emptyRegisterForm);
      setTab("login");
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "No se pudo completar el registro.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyMfa = async (event) => {
    event.preventDefault();
    setMessage(null);
    if (!mfaCode.trim()) {
      setMessage({
        type: "error",
        text: "Ingresa el código de tu app de autenticación.",
      });
      return;
    }
    try {
      setIsSubmitting(true);
      await verifyMfa(mfaCode.trim());
      navigate(redirectPath || "/", { replace: true });
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "El código ingresado no es válido.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderMessage = () =>
    message ? (
      <div
        className={`mt-4 rounded-md px-3 py-2 text-sm ${
          message.type === "error"
            ? "bg-red-100 text-red-700"
            : message.type === "success"
            ? "bg-green-100 text-green-700"
            : "bg-blue-100 text-blue-700"
        }`}
      >
        {message.text}
      </div>
    ) : null;

  return (
    <div
      className={`relative flex min-h-screen items-center justify-center overflow-hidden ${theme.background}`}
    >
      <img
        src={robot}
        alt="Robot Microinvest"
        className="absolute inset-0 h-full w-full object-cover opacity-20"
      />
      <div className="relative z-10 flex w-full max-w-4xl flex-col md:flex-row md:items-stretch md:justify-between">
        <div className="hidden w-1/2 flex-col justify-center px-12 text-white md:flex">
          <h2 className="text-4xl font-bold leading-tight">
            {variant === "empresas"
              ? "Potencia la gestión financiera de tu empresa."
              : "Organiza tus finanzas personales con inteligencia."}
          </h2>
          <p className="mt-4 text-lg">
            {variant === "empresas"
              ? "Accede a herramientas seguras con autenticación multifactor y mantén los datos de tu organización protegidos."
              : "Protege tus finanzas con autenticación segura, gestión de tokens y control total desde un solo lugar."}
          </p>
        </div>
        <div
          className={`relative w-full rounded-3xl p-8 shadow-2xl md:w-1/2 ${theme.panelBg}`}
        >
          <div className="flex flex-col items-center">
            <img
              src={mouUniversal}
              alt="Logotipo Mou"
              className="w-48 object-contain"
            />
            <h1 className={`mt-4 text-2xl font-semibold ${theme.accentText}`}>
              {title}
            </h1>
            <div className="mt-6 flex w-full gap-2 rounded-full bg-black/5 p-1">
              <button
                type="button"
                onClick={() => setTab("login")}
                className={`flex-1 rounded-full px-4 py-2 text-sm font-semibold transition ${
                  tab === "login"
                    ? `${theme.toggleActiveBg} ${theme.toggleActiveText}`
                    : `${theme.toggleInactiveBg} ${theme.toggleInactiveText}`
                }`}
              >
                Iniciar sesión
              </button>
              <button
                type="button"
                onClick={() => setTab("register")}
                className={`flex-1 rounded-full px-4 py-2 text-sm font-semibold transition ${
                  tab === "register"
                    ? `${theme.toggleActiveBg} ${theme.toggleActiveText}`
                    : `${theme.toggleInactiveBg} ${theme.toggleInactiveText}`
                }`}
              >
                Registrarme
              </button>
            </div>
          </div>

          {tab === "login" && (
            <form className="mt-6 space-y-4" onSubmit={handleLogin}>
              <div>
                <label className={`text-sm font-semibold ${theme.accentText}`}>
                  Correo electrónico
                </label>
                <input
                  type="email"
                  autoComplete="email"
                  className={`mt-1 w-full rounded-lg border-none px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 ${theme.inputBg}`}
                  placeholder="ejemplo@correo.com"
                  value={loginForm.correoElectronico}
                  onChange={updateLoginForm("correoElectronico")}
                />
              </div>
              <div>
                <label className={`text-sm font-semibold ${theme.accentText}`}>
                  Contraseña
                </label>
                <input
                  type="password"
                  autoComplete="current-password"
                  className={`mt-1 w-full rounded-lg border-none px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 ${theme.inputBg}`}
                  placeholder="••••••••"
                  value={loginForm.password}
                  onChange={updateLoginForm("password")}
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full rounded-lg px-4 py-2 font-semibold transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${theme.buttonPrimary} ${theme.buttonText} ${
                  isSubmitting ? "opacity-80" : ""
                }`}
              >
                {isSubmitting ? "Procesando..." : "Continuar"}
              </button>
              <p className={`text-xs ${theme.mutedText}`}>
                Al continuar aceptas nuestra política de privacidad y el uso de
                tokens seguros para proteger tu información.
              </p>
            </form>
          )}

          {tab === "register" && (
            <form className="mt-6 space-y-4" onSubmit={handleRegister}>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label
                    className={`text-sm font-semibold ${theme.accentText}`}
                  >
                    Primer nombre
                  </label>
                  <input
                    type="text"
                    className={`mt-1 w-full rounded-lg border-none px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 ${theme.inputBg}`}
                    value={registerForm.primerNombre}
                    onChange={updateRegisterForm("primerNombre")}
                  />
                </div>
                <div>
                  <label
                    className={`text-sm font-semibold ${theme.accentText}`}
                  >
                    Segundo nombre
                  </label>
                  <input
                    type="text"
                    className={`mt-1 w-full rounded-lg border-none px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 ${theme.inputBg}`}
                    value={registerForm.segundoNombre}
                    onChange={updateRegisterForm("segundoNombre")}
                  />
                </div>
                <div>
                  <label
                    className={`text-sm font-semibold ${theme.accentText}`}
                  >
                    Primer apellido
                  </label>
                  <input
                    type="text"
                    className={`mt-1 w-full rounded-lg border-none px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 ${theme.inputBg}`}
                    value={registerForm.primerApellido}
                    onChange={updateRegisterForm("primerApellido")}
                  />
                </div>
                <div>
                  <label
                    className={`text-sm font-semibold ${theme.accentText}`}
                  >
                    Segundo apellido
                  </label>
                  <input
                    type="text"
                    className={`mt-1 w-full rounded-lg border-none px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 ${theme.inputBg}`}
                    value={registerForm.segundoApellido}
                    onChange={updateRegisterForm("segundoApellido")}
                  />
                </div>
              </div>
              <div>
                <label className={`text-sm font-semibold ${theme.accentText}`}>
                  Correo electrónico
                </label>
                <input
                  type="email"
                  autoComplete="email"
                  className={`mt-1 w-full rounded-lg border-none px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 ${theme.inputBg}`}
                  value={registerForm.correoElectronico}
                  onChange={updateRegisterForm("correoElectronico")}
                />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label
                    className={`text-sm font-semibold ${theme.accentText}`}
                  >
                    Contraseña
                  </label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    className={`mt-1 w-full rounded-lg border-none px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 ${theme.inputBg}`}
                    value={registerForm.password}
                    onChange={updateRegisterForm("password")}
                  />
                </div>
                <div>
                  <label
                    className={`text-sm font-semibold ${theme.accentText}`}
                  >
                    Confirmar contraseña
                  </label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    className={`mt-1 w-full rounded-lg border-none px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 ${theme.inputBg}`}
                    value={registerForm.confirmPassword}
                    onChange={updateRegisterForm("confirmPassword")}
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full rounded-lg px-4 py-2 font-semibold transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${theme.buttonPrimary} ${theme.buttonText} ${
                  isSubmitting ? "opacity-80" : ""
                }`}
              >
                {isSubmitting ? "Procesando..." : "Crear cuenta"}
              </button>
              <p className={`text-xs ${theme.mutedText}`}>
                Tus credenciales se almacenarán cifradas y la emisión de tokens
                se gestiona de acuerdo con nuestro servicio de autenticación.
              </p>
            </form>
          )}

          {tab === "mfa" && (
            <form className="mt-6 space-y-4" onSubmit={handleVerifyMfa}>
              <div className={`${theme.mfaBannerBg} rounded-xl px-4 py-3 text-sm text-white`}>
                Introduce el código generado en tu aplicación de autenticación
                para completar el acceso seguro.
              </div>
              <div>
                <label className={`text-sm font-semibold ${theme.accentText}`}>
                  Código MFA
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className={`mt-1 w-full rounded-lg border-none px-3 py-2 text-center text-lg tracking-widest shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 ${theme.inputBg}`}
                  placeholder="123456"
                  value={mfaCode}
                  onChange={(event) => setMfaCode(event.target.value)}
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full rounded-lg px-4 py-2 font-semibold transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${theme.buttonPrimary} ${theme.buttonText} ${
                  isSubmitting ? "opacity-80" : ""
                }`}
              >
                {isSubmitting ? "Verificando..." : "Verificar y continuar"}
              </button>
              <button
                type="button"
                onClick={() => {
                  cancelPendingMfa();
                  setMfaCode("");
                  setTab("login");
                  setMessage(null);
                }}
                className={`w-full rounded-lg border-2 border-dashed px-4 py-2 text-sm font-semibold ${theme.toggleInactiveText}`}
                disabled={isSubmitting}
              >
                Regresar al inicio de sesión
              </button>
            </form>
          )}

          {renderMessage()}
        </div>
      </div>
    </div>
  );
};

export default AuthPageTemplate;
